import { useEffect, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { MapPinOff } from 'lucide-react';
import { Button, EmptyState } from '@paige/ui';
import { useAuth, useAuthStore } from '../auth/AuthBoundary';
import { StateMark } from '../screens/failure/StateMark';
import { showingNotFound } from './page-title';

/**
 * No route at this address: one line, and the way on. Home picks a signed-in person's company and
 * its jobs; a visitor who is not signed in has no jobs to go back to and is offered sign-in
 * (review 2026-10-06: the public 404 said "Go to your jobs").
 */
export function NotFound(): ReactNode {
  const store = useAuthStore();
  const { status } = useAuth();
  // A public address loads nobody by itself; ask once, so a signed-in person is offered their jobs.
  useEffect(() => {
    void store.ensureOrNone();
  }, [store]);
  // The tab says so too, for as long as this is what is on screen.
  useEffect(() => {
    showingNotFound(true);
    return () => showingNotFound(false);
  }, []);
  const signedIn = status === 'signed-in';
  return (
    <div style={{ padding: 'var(--paige-space-6)' }}>
      <EmptyState
        illustration={<StateMark icon={MapPinOff} />}
        title="There is nothing at this address"
        description={
          signedIn
            ? 'Check the link, or go back to your jobs.'
            : 'Check the link, or sign in to Paige.'
        }
        action={
          <Button asChild variant="primary">
            {signedIn ? (
              <Link to="/">Go to your jobs</Link>
            ) : (
              <Link to="/sign-in" search={{}}>
                Sign in
              </Link>
            )}
          </Button>
        }
      />
    </div>
  );
}
